/**
 * DivisionScreen: GRID 모드 — 분할 화면 레이아웃.
 *
 * v1 원본: RenewalComponents/SVContent/DivisionScreen/DivisionScreenContainer.js
 *          + DivisionScreenPresenter.js + DivisionItem/
 * v2 변환: 단일 함수형 컴포넌트 (Container/Presenter 통합)
 *
 * 기능:
 * - splitmod_N 그리드 클래스
 * - 페이지네이션 (maxGridSize 기준)
 * - 참여자 정렬 (AUTO/SPEAKER/NAME)
 * - 참여자 클릭 → SIDE 모드 전환
 * - Jigasi 봇·녹화 서버 유저 필터링
 */
import React, { useMemo, useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { mainUserActions } from '@/store/slices/mainUserSlice';
import VideoPanel from '../SubVideo/VideoPanel';
import type { ScreenUser } from '@/types/components';
import { isRecordingBot } from '@/utils/recordingBot';
import { isRecorderMode } from '@/utils/recorderMode';

interface DivisionScreenProps {
  localUserId: string;
  onSelectParticipant?: (userId: string | null) => void;
}

const DivisionScreen: React.FC<DivisionScreenProps> = ({ localUserId, onSelectParticipant }) => {
  const dispatch = useAppDispatch();
  const participantIds = useAppSelector((s) => s.participants.participantIds);
  const participantsMap = useAppSelector((s) => s.participants.participants);
  const maxGridSize = useAppSelector((s) => s.ui.maxGridSize);
  const screenSort = useAppSelector((s) => s.ui.screenSort);
  const speakerOrder = useAppSelector((s) => s.audioIndicator.speakerOrder);
  const isSharing = useAppSelector((s) => s.screenShare.isSharing);

  const page = useAppSelector((s) => s.ui.gridPage);

  // ─── 참여자 목록 구성 (화면 공유 가상 타일 포함) ───────────────────────────
  const allUsers = useMemo(() => {
    const list: ScreenUser[] = [];

    // 로컬 유저 — recorder 모드에서는 자기 자신(아바타)을 녹화 화면에 넣지 않는다.
    if (!isRecorderMode()) {
      list.push({ id: localUserId, isLocal: true });
      // 로컬 화면 공유 타일
      if (isSharing) {
        list.push({ id: localUserId, isLocal: true, isScreenShare: true });
      }
    }

    // 원격 참여자
    const remoteUsers = participantIds
      .map((id) => participantsMap[id])
      .filter((p) => {
        if (!p) return false;
        if (p.isHidden || p.properties?.features_jigasi) return false;
        if (isRecordingBot(p)) return false;
        return true;
      });

    remoteUsers.forEach((p) => {
      list.push({ id: p.id, isLocal: false });
      // 원격 화면 공유 타일
      if (p.tracks.isScreenSharing) {
        list.push({ id: p.id, isLocal: false, isScreenShare: true });
      }
    });

    return list;
  }, [participantIds, participantsMap, localUserId, isSharing]);

  // ─── 정렬 ─────────────────────────────────────────────────────────────────
  const sortedUsers = useMemo(() => {
    const users = [...allUsers];
    if (screenSort === 'SPEAKER' && speakerOrder.length > 0) {
      users.sort((a, b) => {
        const ai = speakerOrder.indexOf(a.id);
        const bi = speakerOrder.indexOf(b.id);
        const aSpeaker = ai !== -1 ? ai : Infinity;
        const bSpeaker = bi !== -1 ? bi : Infinity;
        return aSpeaker - bSpeaker;
      });
      return users;
    }
    if (screenSort === 'NAME') {
      const locals = users.filter((u) => u.isLocal);
      const remotes = users.filter((u) => !u.isLocal);
      remotes.sort((a, b) => {
        const pA = participantsMap[a.id];
        const pB = participantsMap[b.id];
        const nameA = pA?.displayName || pA?.nickname || '';
        const nameB = pB?.displayName || pB?.nickname || '';
        return nameA.localeCompare(nameB, 'ko');
      });
      return [...locals, ...remotes];
    }
    return users;
  }, [allUsers, screenSort, speakerOrder, participantsMap]);

  // ─── 페이지네이션 ─────────────────────────────────────────────────────────
  const maxPage = Math.max(1, Math.ceil(sortedUsers.length / maxGridSize));

  // 페이지 오버플로 보정
  useEffect(() => {
    if (sortedUsers.length === 0) return;
    if (page > maxPage) dispatch(uiActions.setGridPage(maxPage));
  }, [page, maxPage, sortedUsers.length, dispatch]);

  const pagedUsers = useMemo(() => {
    const start = maxGridSize * (page - 1);
    return sortedUsers.slice(start, start + maxGridSize);
  }, [sortedUsers, page, maxGridSize]);

  // ─── 클릭: SIDE 모드로 전환 ───────────────────────────────────────────────
  const handleClickUser = useCallback(
    (userId: string, showScreenShare?: boolean) => {
      dispatch(mainUserActions.pinUser({ userId, showScreenShare: showScreenShare ?? false }));
      dispatch(uiActions.setScreenMode('SIDE'));
      onSelectParticipant?.(userId);
    },
    [dispatch, onSelectParticipant],
  );

  const handlePagePrev = useCallback(() => {
    dispatch(uiActions.setGridPage(Math.max(1, page - 1)));
  }, [dispatch, page]);

  const handlePageNext = useCallback(() => {
    dispatch(uiActions.setGridPage(Math.min(maxPage, page + 1)));
  }, [dispatch, page, maxPage]);

  return (
    <div className={`usermov_wrap usermov_wrapNew${maxPage > 1 ? ' in_page' : ''}`}>
      {/* 페이지네이션 */}
      <div className="page_bx">
        {page > 1 && (
          <button type="button" className="page_prev" onClick={handlePagePrev}>
            <span className="blind">prev page</span>
          </button>
        )}
        {maxPage > page && (
          <button type="button" className="page_next" onClick={handlePageNext}>
            <span className="blind">next page</span>
          </button>
        )}
      </div>
      {/* 그리드 */}
      <div className={`user_mov customView splitmod_${pagedUsers.length}`}>
        <div className="scrolled">
          {pagedUsers.map((user) => (
            <VideoPanel
              key={user.isScreenShare ? `${user.id}-screen` : user.id}
              participant={user.isLocal ? 'local' : participantsMap[user.id]}
              localUserId={localUserId}
              isMain={false}
              onSelect={handleClickUser}
              showScreenShare={user.isScreenShare}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DivisionScreen;
